import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, eventsApi, tournamentApi, searchApi, assetUrl, bodiesOfWaterApi } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { IconFish } from '../components/Icons';
import { eventStatusLabel } from '../utils/eventStatus';

const emptyUserForm = { first_name: '', last_name: '', email: '', phone: '', password: '' };

// ─── REUSABLE: Sortable column header ──────────────────────
function SortTh({ label, sortKey, sortBy, sortOrder, onSort, style }) {
  const active = sortBy === sortKey;
  const arrow = active ? (sortOrder === 'ASC' ? ' \u25B2' : ' \u25BC') : '';
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
    >
      {label}{arrow}
    </th>
  );
}

// ─── REUSABLE: Pagination bar with rows-per-page dropdown ──
function PaginationBar({ page, totalPages, total, limit, onPageChange, onLimitChange }) {
  if (total === 0) return null;
  const limitOptions = [10, 20, 50, 100];
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #2a3f56', flexWrap: 'wrap', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>Rows per page:</span>
        <select
          value={limit}
          onChange={e => onLimitChange(parseInt(e.target.value))}
          style={{ padding: '4px 8px', background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 6, color: '#f0f2f5', fontSize: '0.82rem' }}
        >
          {limitOptions.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span style={{ color: '#5c6d83', fontSize: '0.78rem' }}>({total} total)</span>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} style={{ padding: '4px 12px', fontSize: '0.78rem' }}>Previous</button>
          <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} style={{ padding: '4px 12px', fontSize: '0.78rem' }}>Next</button>
        </div>
      )}
    </div>
  );
}

// ─── Hook: pagination + sorting state ──────────────────────
function useAdminTable(fetchFn, defaultLimit = 20) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('DESC');

  const handleSort = useCallback((key) => {
    if (sortBy === key) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
    setPage(1);
  }, [sortBy]);

  const handleLimitChange = useCallback((newLimit) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const totalPages = Math.ceil(total / limit);

  return { page, setPage, limit, total, setTotal, sortBy, sortOrder, handleSort, handleLimitChange, totalPages };
}

// ─── ORGANIZERS PAGE ─────────────────────────────────────────
export function OrganizersPage() {
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyUserForm });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { page, setPage, limit, total, setTotal, sortBy, sortOrder, handleSort, handleLimitChange, totalPages } = useAdminTable(null);

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { fetchOrganizers(); }, [search, page, limit, sortBy, sortOrder]);

  async function fetchOrganizers() {
    setLoading(true);
    try {
      const params = { search, page, limit };
      if (sortBy) { params.sortBy = sortBy; params.sortOrder = sortOrder; }
      const { data } = await adminApi.getOrganizers(params);
      setOrganizers(data.data);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function toggleStatus(id, currentActive) {
    try {
      await adminApi.updateOrganizerStatus(id, { is_active: !currentActive });
      fetchOrganizers();
    } catch (err) {
      alert('Failed to update status');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this organizer? If they have events, their account will be anonymized instead.')) return;
    try {
      const { data } = await adminApi.deleteOrganizer(id);
      alert(data.anonymized ? 'Organizer was anonymized (has existing events).' : 'Organizer deleted.');
      fetchOrganizers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete organizer');
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        const { password, ...fields } = form;
        await adminApi.updateOrganizer(editingId, fields);
      } else {
        await adminApi.createOrganizer(form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyUserForm });
      fetchOrganizers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save organizer');
    }
  }

  function startEdit(org) {
    setEditingId(org.id);
    setForm({ first_name: org.first_name, last_name: org.last_name, email: org.email, phone: org.phone || '', password: '' });
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyUserForm });
    setError('');
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header page-header-row">
          <div>
            <h1>Event Organizers</h1>
            <p>Manage all registered event organizers</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => showForm ? cancelForm() : setShowForm(true)}>
            {showForm ? 'Cancel' : '+ New Organizer'}
          </button>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 16 }}>{editingId ? 'Edit Organizer' : 'New Organizer'}</h4>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSave}>
              <div className="grid-2col">
                <div className="form-group">
                  <label>First Name</label>
                  <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                {!editingId && (
                  <div className="form-group">
                    <label>Password</label>
                    <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editingId} />
                  </div>
                )}
              </div>
              <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>{editingId ? 'Save Changes' : 'Create Organizer'}</button>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="form-input"
            style={{ width: '100%', padding: '10px 14px', background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, color: '#f0f2f5', fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.88rem' }}
          />
        </div>

        {loading ? <div className="spinner" /> : (
          <div className="card">
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <SortTh label="Name" sortKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Email</th><th>Phone</th>
                    <SortTh label="Events" sortKey="events" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Total Spent</th>
                    <SortTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {organizers.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No organizers found</td></tr>
                  ) : organizers.map(org => (
                    <tr key={org.id}>
                      <td style={{ fontWeight: 500 }}>{org.first_name} {org.last_name}</td>
                      <td data-label="Email" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{org.email}</td>
                      <td data-label="Phone" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{org.phone || '—'}</td>
                      <td data-label="Events">{org.total_events}</td>
                      <td data-label="Total Spent" style={{ color: '#e8a838' }}>${parseFloat(org.total_spent || 0).toFixed(2)}</td>
                      <td data-label="Status"><span className={`badge ${org.is_active ? 'badge-active' : 'badge-draft'}`}>{org.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(org)}>Edit</button>
                          <button
                            className={`btn btn-sm ${org.is_active ? 'btn-secondary' : 'btn-primary'}`}
                            onClick={() => toggleStatus(org.id, org.is_active)}
                          >
                            {org.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleDelete(org.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={handleLimitChange} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PARTICIPANTS PAGE ──────────────────────────────────────
export function ParticipantsPage() {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyUserForm });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const editFormRef = useRef(null);
  const { page, setPage, limit, total, setTotal, sortBy, sortOrder, handleSort, handleLimitChange, totalPages } = useAdminTable(null);

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { fetchParticipants(); }, [search, page, limit, sortBy, sortOrder]);

  async function fetchParticipants() {
    setLoading(true);
    try {
      const params = { search, page, limit };
      if (sortBy) { params.sortBy = sortBy; params.sortOrder = sortOrder; }
      const { data } = await adminApi.getParticipants(params);
      setParticipants(data.data);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function toggleStatus(id, currentActive) {
    try {
      await adminApi.updateParticipantStatus(id, { is_active: !currentActive });
      fetchParticipants();
    } catch (err) {
      alert('Failed to update status');
    }
  }

  async function handleDeleteParticipant(id) {
    if (!window.confirm('Delete this participant? If they have event data, their account will be anonymized instead.')) return;
    try {
      const { data } = await adminApi.deleteParticipant(id);
      alert(data.anonymized ? 'Participant was anonymized (has existing event data).' : 'Participant deleted.');
      fetchParticipants();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete participant');
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        const { password, ...fields } = form;
        await adminApi.updateParticipant(editingId, fields);
      } else {
        await adminApi.createParticipant(form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyUserForm });
      fetchParticipants();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save participant');
    }
  }

  function startEdit(p) {
    setEditingId(p.id);
    setForm({ first_name: p.first_name, last_name: p.last_name, email: p.email, phone: p.phone || '', password: '' });
    setShowForm(true);
    setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyUserForm });
    setError('');
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header page-header-row">
          <div>
            <h1>Participants</h1>
            <p>Manage all registered participants</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => showForm ? cancelForm() : setShowForm(true)}>
            {showForm ? 'Cancel' : '+ New Participant'}
          </button>
        </div>

        {showForm && (
          <div ref={editFormRef} className="card" style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 16 }}>{editingId ? 'Edit Participant' : 'New Participant'}</h4>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSave}>
              <div className="grid-2col">
                <div className="form-group">
                  <label>First Name</label>
                  <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                {!editingId && (
                  <div className="form-group">
                    <label>Password</label>
                    <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editingId} />
                  </div>
                )}
              </div>
              <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>{editingId ? 'Save Changes' : 'Create Participant'}</button>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, color: '#f0f2f5', fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.88rem' }}
          />
        </div>

        {loading ? <div className="spinner" /> : (
          <div className="card">
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <SortTh label="Name" sortKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Email</th><th>Phone</th>
                    <SortTh label="Type" sortKey="type" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortTh label="Events" sortKey="events" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortTh label="Teams" sortKey="teams" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No participants found</td></tr>
                  ) : participants.map(p => (
                    <React.Fragment key={p.id}>
                      <tr onClick={() => setExpandedId(expandedId === p.id ? null : p.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 500 }}>{p.first_name} {p.last_name}</td>
                        <td data-label="Email" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{p.email}</td>
                        <td data-label="Phone" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{p.phone || '—'}</td>
                        <td data-label="Type"><span className={`badge ${p.role === 'organizer' ? 'badge-live' : 'badge-pending'}`} style={{ fontSize: '0.7rem' }}>{p.role === 'organizer' ? 'Organizer' : 'Participant'}</span></td>
                        <td data-label="Events">{p.total_events || 0}</td>
                        <td data-label="Teams">{p.total_teams || 0}</td>
                        <td data-label="Status"><span className={`badge ${p.is_active ? 'badge-active' : 'badge-draft'}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); startEdit(p); }}>Edit</button>
                            <button
                              className={`btn btn-sm ${p.is_active ? 'btn-secondary' : 'btn-primary'}`}
                              onClick={(e) => { e.stopPropagation(); toggleStatus(p.id, p.is_active); }}
                            >
                              {p.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={(e) => { e.stopPropagation(); handleDeleteParticipant(p.id); }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === p.id && (
                        <tr>
                          <td colSpan={8} style={{ background: '#1a2a3a', padding: 16 }}>
                            <ParticipantDetail participantId={p.id} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={handleLimitChange} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PARTICIPANT DETAIL (expandable sub-component) ──────────
function ParticipantDetail({ participantId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await adminApi.getParticipantDetail(participantId);
        setDetail(data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [participantId]);

  if (loading) return <div className="spinner" style={{ margin: '12px 0' }} />;
  if (!detail) return <div style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>Failed to load details</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
      <div>
        <h4 style={{ marginBottom: 8, fontSize: '0.9rem', color: 'var(--amber, #e8a838)' }}>Teams</h4>
        {detail.teams.length === 0 ? (
          <div style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>No team memberships</div>
        ) : (
          <table style={{ width: '100%', fontSize: '0.82rem' }}>
            <thead><tr style={{ color: '#8a9bb5' }}><th style={{ textAlign: 'left', padding: '4px 8px' }}>Team</th><th style={{ textAlign: 'left', padding: '4px 8px' }}>Event</th><th style={{ textAlign: 'left', padding: '4px 8px' }}>Role</th><th style={{ textAlign: 'left', padding: '4px 8px' }}>#</th></tr></thead>
            <tbody>
              {detail.teams.map((t, i) => (
                <tr key={i}>
                  <td style={{ padding: '4px 8px', fontWeight: 500 }}>{t.team_name}</td>
                  <td style={{ padding: '4px 8px', color: '#8a9bb5' }}>{t.event_name}</td>
                  <td style={{ padding: '4px 8px' }}><span className={`badge ${t.role === 'captain' ? 'badge-active' : 'badge-pending'}`} style={{ fontSize: '0.72rem' }}>{t.role}</span></td>
                  <td style={{ padding: '4px 8px', color: '#8a9bb5' }}>{t.team_number || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <h4 style={{ marginBottom: 8, fontSize: '0.9rem', color: 'var(--amber, #e8a838)' }}>Event Registrations</h4>
        {detail.events.length === 0 ? (
          <div style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>No event registrations</div>
        ) : (
          <table style={{ width: '100%', fontSize: '0.82rem' }}>
            <thead><tr style={{ color: '#8a9bb5' }}><th style={{ textAlign: 'left', padding: '4px 8px' }}>Event</th><th style={{ textAlign: 'left', padding: '4px 8px' }}>Status</th><th style={{ textAlign: 'left', padding: '4px 8px' }}>Date</th></tr></thead>
            <tbody>
              {detail.events.map((ev, i) => (
                <tr key={i}>
                  <td style={{ padding: '4px 8px', fontWeight: 500 }}>{ev.event_name}</td>
                  <td style={{ padding: '4px 8px' }}><span className={`badge ${ev.registration_status === 'confirmed' ? 'badge-active' : 'badge-pending'}`} style={{ fontSize: '0.72rem' }}>{ev.registration_status}</span></td>
                  <td style={{ padding: '4px 8px', color: '#8a9bb5' }}>{ev.registered_at ? new Date(ev.registered_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── TEAMS PAGE ─────────────────────────────────────────────
export function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [expandLoading, setExpandLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ team_name: '', team_number: '', max_members: 4, captain_user_id: '' });
  const [error, setError] = useState('');
  const [captainSearch, setCaptainSearch] = useState('');
  const [captainResults, setCaptainResults] = useState([]);
  const [captainSearchLoading, setCaptainSearchLoading] = useState(false);
  const [editCaptainMode, setEditCaptainMode] = useState('search'); // 'search' | 'new'
  const [editNewCaptainName, setEditNewCaptainName] = useState('');
  const [editNewCaptainEmail, setEditNewCaptainEmail] = useState('');
  const editFormRef = useRef(null);

  // Create team state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ team_name: '', event_id: '', max_members: 4, captain_user_id: '' });
  const [createError, setCreateError] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [createCaptainSearch, setCreateCaptainSearch] = useState('');
  const [createCaptainResults, setCreateCaptainResults] = useState([]);
  const [createCaptainLoading, setCreateCaptainLoading] = useState(false);
  const [createCaptainMode, setCreateCaptainMode] = useState('search'); // 'search' | 'new'
  const [createNewCaptainName, setCreateNewCaptainName] = useState('');
  const [createNewCaptainEmail, setCreateNewCaptainEmail] = useState('');
  const { page, setPage, limit, total, setTotal, sortBy, sortOrder, handleSort, handleLimitChange, totalPages } = useAdminTable(null);

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { fetchTeams(); }, [search, page, limit, sortBy, sortOrder]);
  useEffect(() => {
    if (showCreate && allEvents.length === 0) {
      adminApi.getEvents({ limit: 500 }).then(r => setAllEvents(r.data.data || [])).catch(() => {});
    }
  }, [showCreate]);

  async function fetchTeams() {
    setLoading(true);
    try {
      const params = { search, page, limit };
      if (sortBy) { params.sortBy = sortBy; params.sortOrder = sortOrder; }
      const { data } = await adminApi.getTeams(params);
      setTeams(data.data);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleExpand(id) {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedTeam(null);
      return;
    }
    setExpandedId(id);
    setExpandLoading(true);
    try {
      const { data } = await adminApi.getTeam(id);
      setExpandedTeam(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setExpandLoading(false);
    }
  }

  function startEdit(team) {
    setEditingId(team.id);
    setEditForm({
      team_name: team.team_name,
      team_number: team.team_number || '',
      system_number: team.system_number || null,
      max_members: team.max_members || 4,
      captain_user_id: team.captain_user_id || team.captain_id || ''
    });
    setCaptainSearch(team.captain_name || '');
    setCaptainResults([]);
    setEditCaptainMode('search');
    setEditNewCaptainName('');
    setEditNewCaptainEmail('');
    setError('');
    setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({ team_name: '', team_number: '', system_number: null, max_members: 4, captain_user_id: '' });
    setCaptainSearch('');
    setCaptainResults([]);
    setEditCaptainMode('search');
    setEditNewCaptainName('');
    setEditNewCaptainEmail('');
    setError('');
  }

  async function handleCaptainSearch(value) {
    setCaptainSearch(value);
    if (!value || value.length < 2) { setCaptainResults([]); return; }
    setCaptainSearchLoading(true);
    try {
      const { data } = await searchApi.searchUsers(value);
      setCaptainResults(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setCaptainSearchLoading(false);
    }
  }

  function selectCaptain(user) {
    setEditForm({ ...editForm, captain_user_id: user.id });
    setCaptainSearch(`${user.first_name} ${user.last_name}`);
    setCaptainResults([]);
  }

  function clearCaptain() {
    setEditForm({ ...editForm, captain_user_id: '' });
    setCaptainSearch('');
    setCaptainResults([]);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (editCaptainMode === 'new' && editNewCaptainEmail && !editNewCaptainName.trim()) {
      setError('Captain name is required when adding a new participant');
      return;
    }
    setError('');
    try {
      const payload = {
        team_name: editForm.team_name,
        team_number: editForm.team_number === '' ? null : parseInt(editForm.team_number),
        max_members: parseInt(editForm.max_members),
      };
      if (editCaptainMode === 'search') {
        payload.captain_user_id = editForm.captain_user_id || null;
      } else {
        payload.captain_new_name = editNewCaptainName.trim() || null;
        payload.captain_new_email = editNewCaptainEmail.trim() || null;
      }
      await adminApi.updateTeam(editingId, payload);
      setEditingId(null);
      fetchTeams();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update team');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this team? This will permanently remove all associated catches, scores, and tournament entries.')) return;
    try {
      await adminApi.deleteTeam(id);
      if (expandedId === id) { setExpandedId(null); setExpandedTeam(null); }
      fetchTeams();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete team');
    }
  }

  async function handleToggleActive(team) {
    try {
      await adminApi.updateTeam(team.id, { is_active: !team.is_active });
      fetchTeams();
    } catch (err) {
      alert('Failed to update team status');
    }
  }

  async function handleCreateCaptainSearch(value) {
    setCreateCaptainSearch(value);
    if (!value || value.length < 2) { setCreateCaptainResults([]); return; }
    setCreateCaptainLoading(true);
    try {
      const { data } = await searchApi.searchUsers(value);
      setCreateCaptainResults(data.data || []);
    } catch { /* ignore */ }
    finally { setCreateCaptainLoading(false); }
  }

  async function handleCreateTeam(e) {
    e.preventDefault();
    if (!createForm.team_name.trim()) {
      setCreateError('Team name is required');
      return;
    }
    if (createCaptainMode === 'new' && createNewCaptainEmail && !createNewCaptainName.trim()) {
      setCreateError('Captain name is required when adding a new participant');
      return;
    }
    setCreateSaving(true);
    setCreateError('');
    try {
      const payload = {
        team_name: createForm.team_name.trim(),
        event_id: createForm.event_id || null,
        max_members: parseInt(createForm.max_members) || 4,
      };
      if (createCaptainMode === 'search') {
        payload.captain_user_id = createForm.captain_user_id || null;
      } else {
        payload.captain_new_name = createNewCaptainName.trim() || null;
        payload.captain_new_email = createNewCaptainEmail.trim() || null;
      }
      await adminApi.createTeam(payload);
      setShowCreate(false);
      setCreateForm({ team_name: '', event_id: '', max_members: 4, captain_user_id: '' });
      setCreateCaptainSearch('');
      setCreateCaptainResults([]);
      setCreateCaptainMode('search');
      setCreateNewCaptainName('');
      setCreateNewCaptainEmail('');
      fetchTeams();
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create team');
    } finally {
      setCreateSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1>Teams</h1>
            <p>Manage teams across all events</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowCreate(!showCreate); setCreateError(''); }}>
            {showCreate ? 'Cancel' : '+ Add Team'}
          </button>
        </div>

        {/* Create team form */}
        {showCreate && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 16 }}>Add Team</h4>
            {createError && <div className="alert alert-error">{createError}</div>}
            <form onSubmit={handleCreateTeam}>
              <div className="form-group">
                <label>Event <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
                <select
                  value={createForm.event_id}
                  onChange={e => setCreateForm(f => ({ ...f, event_id: e.target.value }))}
                  style={{ width: '100%', padding: '10px 14px', background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, color: '#f0f2f5', fontSize: '0.88rem' }}
                >
                  <option value="">No event (standalone team)</option>
                  {allEvents.map(ev => (
                    <option key={ev.id} value={ev.id}>{ev.name} ({ev.status})</option>
                  ))}
                </select>
              </div>
              <div className="grid-4col">
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Team Name *</label>
                  <input value={createForm.team_name} onChange={e => setCreateForm(f => ({ ...f, team_name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Max Members</label>
                  <input type="number" min={1} value={createForm.max_members} onChange={e => setCreateForm(f => ({ ...f, max_members: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Captain <span style={{ color: '#5c6d83', fontWeight: 400 }}>(optional)</span></label>
                <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderRadius: 6, overflow: 'hidden', border: '1px solid #2a3f56', width: 'fit-content' }}>
                  <button type="button"
                    style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: createCaptainMode === 'search' ? '#4a9eff' : '#1e2d3d', color: createCaptainMode === 'search' ? '#fff' : '#8a9bb5' }}
                    onClick={() => setCreateCaptainMode('search')}>Search Existing</button>
                  <button type="button"
                    style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: createCaptainMode === 'new' ? '#e8a838' : '#1e2d3d', color: createCaptainMode === 'new' ? '#0d1b2a' : '#8a9bb5' }}
                    onClick={() => setCreateCaptainMode('new')}>Add New Participant</button>
                </div>
                {createCaptainMode === 'search' ? (
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="Search users by name or email..."
                        value={createCaptainSearch}
                        onChange={e => handleCreateCaptainSearch(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      {createForm.captain_user_id && (
                        <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#e85d5d', whiteSpace: 'nowrap' }} onClick={() => { setCreateForm(f => ({ ...f, captain_user_id: '' })); setCreateCaptainSearch(''); setCreateCaptainResults([]); }}>Remove</button>
                      )}
                    </div>
                    {createCaptainLoading && <div style={{ fontSize: '0.78rem', color: '#8a9bb5', marginTop: 4 }}>Searching...</div>}
                    {createCaptainResults.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
                        {createCaptainResults.map(u => (
                          <div
                            key={u.id}
                            onClick={() => { setCreateForm(f => ({ ...f, captain_user_id: u.id })); setCreateCaptainSearch(`${u.first_name} ${u.last_name}`); setCreateCaptainResults([]); }}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #2a3f56' }}
                            onMouseOver={e => e.currentTarget.style.background = '#253a4e'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</span>
                            <span style={{ color: '#8a9bb5', marginLeft: 8 }}>{u.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <input
                      type="text"
                      placeholder="Full name *"
                      value={createNewCaptainName}
                      onChange={e => setCreateNewCaptainName(e.target.value)}
                    />
                    <input
                      type="email"
                      placeholder="Email address *"
                      value={createNewCaptainEmail}
                      onChange={e => setCreateNewCaptainEmail(e.target.value)}
                    />
                    <div style={{ gridColumn: 'span 2', fontSize: '0.75rem', color: '#8a9bb5' }}>
                      Registration Confirmed and Team Invitation emails will be sent to this address.
                    </div>
                  </div>
                )}
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={createSaving} style={{ marginTop: 8 }}>
                {createSaving ? 'Creating...' : 'Create Team'}
              </button>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <input
            type="text"
            placeholder="Search by team name, captain, or event..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, color: '#f0f2f5', fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.88rem' }}
          />
        </div>

        {/* Inline edit form */}
        {editingId && (
          <div ref={editFormRef} className="card" style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 16 }}>Edit Team</h4>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSave}>
              <div className="grid-4col">
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Team Name</label>
                  <input value={editForm.team_name} onChange={e => setEditForm({ ...editForm, team_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>System # <span style={{ color: '#5c6d83', fontWeight: 400, fontSize: '0.72rem' }}>(auto)</span></label>
                  <input value={editForm.system_number ? `#${editForm.system_number}` : 'Auto-assigned'} readOnly style={{ color: '#4a9eff', cursor: 'not-allowed', opacity: 0.7 }} />
                </div>
                <div className="form-group">
                  <label>Max Members</label>
                  <input type="number" min={1} value={editForm.max_members} onChange={e => setEditForm({ ...editForm, max_members: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Event # <span style={{ color: '#5c6d83', fontWeight: 400, fontSize: '0.72rem' }}>(event-specific)</span></label>
                  <input type="number" value={editForm.team_number} onChange={e => setEditForm({ ...editForm, team_number: e.target.value })} placeholder="Auto" />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Captain</label>
                <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderRadius: 6, overflow: 'hidden', border: '1px solid #2a3f56', width: 'fit-content' }}>
                  <button type="button"
                    style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: editCaptainMode === 'search' ? '#4a9eff' : '#1e2d3d', color: editCaptainMode === 'search' ? '#fff' : '#8a9bb5' }}
                    onClick={() => setEditCaptainMode('search')}>Search Existing</button>
                  <button type="button"
                    style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: editCaptainMode === 'new' ? '#e8a838' : '#1e2d3d', color: editCaptainMode === 'new' ? '#0d1b2a' : '#8a9bb5' }}
                    onClick={() => setEditCaptainMode('new')}>Add New Participant</button>
                </div>
                {editCaptainMode === 'search' ? (
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="Search users by name or email..."
                        value={captainSearch}
                        onChange={e => handleCaptainSearch(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      {editForm.captain_user_id && (
                        <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#e85d5d', whiteSpace: 'nowrap' }} onClick={clearCaptain}>Remove</button>
                      )}
                    </div>
                    {captainSearchLoading && <div style={{ fontSize: '0.78rem', color: '#8a9bb5', marginTop: 4 }}>Searching...</div>}
                    {captainResults.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
                        {captainResults.map(u => (
                          <div
                            key={u.id}
                            onClick={() => selectCaptain(u)}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #2a3f56' }}
                            onMouseOver={e => e.currentTarget.style.background = '#253a4e'}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</span>
                            <span style={{ color: '#8a9bb5', marginLeft: 8 }}>{u.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <input
                      type="text"
                      placeholder="Full name *"
                      value={editNewCaptainName}
                      onChange={e => setEditNewCaptainName(e.target.value)}
                    />
                    <input
                      type="email"
                      placeholder="Email address *"
                      value={editNewCaptainEmail}
                      onChange={e => setEditNewCaptainEmail(e.target.value)}
                    />
                    <div style={{ gridColumn: 'span 2', fontSize: '0.75rem', color: '#8a9bb5' }}>
                      Registration Confirmed and Team Invitation emails will be sent to this address.
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm">Save Changes</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {loading ? <div className="spinner" /> : (
          <div className="card">
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <SortTh label="Team Name" sortKey="team_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Event</th>
                    <SortTh label="Captain" sortKey="captain" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Members</th>
                    <SortTh label="System #" sortKey="system_number" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Event #</th>
                    <SortTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No teams found</td></tr>
                  ) : teams.map(team => (
                    <React.Fragment key={team.id}>
                      <tr onClick={() => handleExpand(team.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 500 }}>{team.team_name}</td>
                        <td data-label="Event" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{team.event_name || '—'}</td>
                        <td data-label="Captain" style={{ fontSize: '0.82rem' }}>{team.captain_name || '—'}</td>
                        <td data-label="Members">{team.member_count}</td>
                        <td data-label="System #" style={{ color: '#4a9eff', fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>{team.system_number ? `#${team.system_number}` : '—'}</td>
                        <td data-label="Event #" style={{ color: '#e8a838' }}>{team.team_number || '—'}</td>
                        <td data-label="Status"><span className={`badge ${team.is_active ? 'badge-active' : 'badge-draft'}`}>{team.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); startEdit(team); }}>Edit</button>
                            <button
                              className={`btn btn-sm ${team.is_active ? 'btn-secondary' : 'btn-primary'}`}
                              onClick={(e) => { e.stopPropagation(); handleToggleActive(team); }}
                            >
                              {team.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={(e) => { e.stopPropagation(); handleDelete(team.id); }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === team.id && (
                        <tr>
                          <td colSpan={7} style={{ background: '#1a2a3a', padding: 16 }}>
                            {expandLoading ? <div className="spinner" style={{ margin: '8px 0' }} /> : expandedTeam ? (
                              <div>
                                <h4 style={{ marginBottom: 8, fontSize: '0.9rem', color: 'var(--amber, #e8a838)' }}>Members</h4>
                                {expandedTeam.members.length === 0 ? (
                                  <div style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>No members</div>
                                ) : (
                                  <table style={{ width: '100%', fontSize: '0.82rem', maxWidth: 500 }}>
                                    <thead><tr style={{ color: '#8a9bb5' }}><th style={{ textAlign: 'left', padding: '4px 8px' }}>Name</th><th style={{ textAlign: 'left', padding: '4px 8px' }}>Email</th><th style={{ textAlign: 'left', padding: '4px 8px' }}>Role</th></tr></thead>
                                    <tbody>
                                      {expandedTeam.members.map(m => (
                                        <tr key={m.id}>
                                          <td style={{ padding: '4px 8px', fontWeight: 500 }}>{m.first_name} {m.last_name}</td>
                                          <td style={{ padding: '4px 8px', color: '#8a9bb5' }}>{m.email}</td>
                                          <td style={{ padding: '4px 8px' }}><span className={`badge ${m.role === 'captain' ? 'badge-active' : 'badge-pending'}`} style={{ fontSize: '0.72rem' }}>{m.role}</span></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            ) : (
                              <div style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>Failed to load team details</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={handleLimitChange} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PRICING TIERS PAGE ──────────────────────────────────────
export function PricingTiersPage() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tier_name: '', min_attendees: 1, max_attendees: 100, price_cad: 49.99 });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');

  // Coupon state
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(true);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState({ code: '', discount_percent: 10, valid_from: '', valid_until: '' });
  const [couponEditingId, setCouponEditingId] = useState(null);
  const [couponError, setCouponError] = useState('');

  useEffect(() => { fetchTiers(); fetchCoupons(); }, []);

  async function fetchTiers() {
    setLoading(true);
    try {
      const { data } = await adminApi.getPricingTiers();
      setTiers(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await adminApi.updatePricingTier(editingId, form);
      } else {
        await adminApi.createPricingTier(form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ tier_name: '', min_attendees: 1, max_attendees: 100, price_cad: 49.99 });
      fetchTiers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save tier');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this pricing tier? Events using it will not be affected.')) return;
    try {
      await adminApi.deletePricingTier(id);
      fetchTiers();
    } catch (err) {
      alert('Failed to delete tier');
    }
  }

  function startEdit(tier) {
    setEditingId(tier.id);
    setForm({ tier_name: tier.tier_name, min_attendees: tier.min_attendees, max_attendees: tier.max_attendees, price_cad: tier.price_cad });
    setShowForm(true);
  }

  // ── Coupon functions ──
  async function fetchCoupons() {
    setCouponsLoading(true);
    try {
      const { data } = await adminApi.getCoupons();
      setCoupons(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setCouponsLoading(false);
    }
  }

  async function handleCouponSave(e) {
    e.preventDefault();
    setCouponError('');
    try {
      if (couponEditingId) {
        await adminApi.updateCoupon(couponEditingId, couponForm);
      } else {
        await adminApi.createCoupon(couponForm);
      }
      setShowCouponForm(false);
      setCouponEditingId(null);
      setCouponForm({ code: '', discount_percent: 10, valid_from: '', valid_until: '' });
      fetchCoupons();
    } catch (err) {
      setCouponError(err.response?.data?.message || 'Failed to save coupon');
    }
  }

  async function handleCouponDelete(id) {
    if (!window.confirm('Delete this coupon? Events already using it will not be affected.')) return;
    try {
      await adminApi.deleteCoupon(id);
      fetchCoupons();
    } catch (err) {
      alert('Failed to delete coupon');
    }
  }

  function startCouponEdit(coupon) {
    setCouponEditingId(coupon.id);
    setCouponForm({
      code: coupon.code,
      discount_percent: coupon.discount_percent,
      valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().slice(0, 16) : '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().slice(0, 16) : ''
    });
    setShowCouponForm(true);
  }

  function cancelCouponForm() {
    setShowCouponForm(false);
    setCouponEditingId(null);
    setCouponForm({ code: '', discount_percent: 10, valid_from: '', valid_until: '' });
    setCouponError('');
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header page-header-row">
          <div>
            <h1>Pricing Tiers</h1>
            <p>Configure attendee limits and pricing. Changes take effect on new events.</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ tier_name: '', min_attendees: 1, max_attendees: 100, price_cad: 49.99 }); }}>
            {showForm ? 'Cancel' : '+ New Tier'}
          </button>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 16 }}>{editingId ? 'Edit Tier' : 'New Pricing Tier'}</h4>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSave}>
              <div className="grid-4col">
                <div className="form-group">
                  <label>Tier Name</label>
                  <input value={form.tier_name} onChange={e => setForm({ ...form, tier_name: e.target.value })} placeholder="Tier 1" required />
                </div>
                <div className="form-group">
                  <label>Min Attendees</label>
                  <input type="number" value={form.min_attendees} onChange={e => setForm({ ...form, min_attendees: parseInt(e.target.value) })} required />
                </div>
                <div className="form-group">
                  <label>Max Attendees</label>
                  <input type="number" value={form.max_attendees} onChange={e => setForm({ ...form, max_attendees: parseInt(e.target.value) })} required />
                </div>
                <div className="form-group">
                  <label>Price (CAD)</label>
                  <input type="number" step="0.01" value={form.price_cad} onChange={e => setForm({ ...form, price_cad: parseFloat(e.target.value) })} required />
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-sm">{editingId ? 'Save Changes' : 'Create Tier'}</button>
            </form>
          </div>
        )}

        {loading ? <div className="spinner" /> : (
          <div className="card">
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr><th>Tier</th><th>Attendees Range</th><th>Price (CAD)</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {tiers.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No pricing tiers configured</td></tr>
                  ) : tiers.map(tier => (
                    <tr key={tier.id}>
                      <td style={{ fontWeight: 600, color: '#e8a838', fontFamily: 'Oswald, sans-serif' }}>{tier.tier_name}</td>
                      <td data-label="Attendees" style={{ color: '#f0f2f5' }}>{tier.min_attendees} – {tier.max_attendees}</td>
                      <td data-label="Price" style={{ color: '#e8a838', fontWeight: 600 }}>${parseFloat(tier.price_cad).toFixed(2)}</td>
                      <td data-label="Status"><span className={`badge ${tier.is_active ? 'badge-active' : 'badge-draft'}`}>{tier.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(tier)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleDelete(tier.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── COUPONS SECTION ── */}
        <div style={{ marginTop: 32 }}>
          <div className="section-header-row">
            <h2 style={{ margin: 0 }}>Coupon Codes</h2>
            <button className="btn btn-primary btn-sm" onClick={() => showCouponForm ? cancelCouponForm() : setShowCouponForm(true)}>
              {showCouponForm ? 'Cancel' : '+ New Coupon'}
            </button>
          </div>

          {showCouponForm && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h4 style={{ marginBottom: 16 }}>{couponEditingId ? 'Edit Coupon' : 'New Coupon'}</h4>
              {couponError && <div className="alert alert-error">{couponError}</div>}
              <form onSubmit={handleCouponSave}>
                <div className="grid-4col">
                  <div className="form-group">
                    <label>Code</label>
                    <input value={couponForm.code} onChange={e => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="FISH20" required />
                  </div>
                  <div className="form-group">
                    <label>Discount %</label>
                    <input type="number" min={1} max={100} value={couponForm.discount_percent} onChange={e => setCouponForm({ ...couponForm, discount_percent: parseInt(e.target.value) || '' })} required />
                  </div>
                  <div className="form-group">
                    <label>Valid From</label>
                    <input type="datetime-local" value={couponForm.valid_from} onChange={e => setCouponForm({ ...couponForm, valid_from: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Valid Until</label>
                    <input type="datetime-local" value={couponForm.valid_until} onChange={e => setCouponForm({ ...couponForm, valid_until: e.target.value })} required />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-sm">{couponEditingId ? 'Save Changes' : 'Create Coupon'}</button>
              </form>
            </div>
          )}

          {couponsLoading ? <div className="spinner" /> : (
            <div className="card">
              <div className="table-wrap">
                <table className="responsive-table">
                  <thead>
                    <tr><th>Code</th><th>Discount</th><th>Valid From</th><th>Valid Until</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {coupons.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No coupons created</td></tr>
                    ) : coupons.map(coupon => {
                      const now = new Date();
                      const isExpired = new Date(coupon.valid_until) < now;
                      const isNotYetValid = new Date(coupon.valid_from) > now;
                      const statusLabel = !coupon.is_active ? 'Inactive' : isExpired ? 'Expired' : isNotYetValid ? 'Scheduled' : 'Active';
                      const statusClass = !coupon.is_active || isExpired ? 'badge-draft' : isNotYetValid ? 'badge-pending' : 'badge-active';
                      return (
                        <tr key={coupon.id}>
                          <td style={{ fontWeight: 600, color: '#e8a838', fontFamily: 'Oswald, sans-serif', letterSpacing: 1 }}>{coupon.code}</td>
                          <td data-label="Discount" style={{ color: '#f0f2f5', fontWeight: 600 }}>{coupon.discount_percent}%</td>
                          <td data-label="From" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{new Date(coupon.valid_from).toLocaleDateString()}</td>
                          <td data-label="Until" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{new Date(coupon.valid_until).toLocaleDateString()}</td>
                          <td data-label="Status"><span className={`badge ${statusClass}`}>{statusLabel}</span></td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn btn-ghost btn-sm" onClick={() => startCouponEdit(coupon)}>Edit</button>
                              <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleCouponDelete(coupon.id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="alert alert-info" style={{ marginTop: 24, fontSize: '0.8rem' }}>
          <strong>Note:</strong> Pricing changes only affect new events. Existing events keep their tier pricing at the time of purchase.
          The 90% capacity warning and automatic upgrade prompts use the current tier limits.
        </div>
      </div>
    </div>
  );
}

// ─── THEME SETTINGS PAGE ────────────────────────────────────
// ─── EMAIL TEMPLATES PAGE ───────────────────────────────────
export function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ subject: '', html_body: '', text_body: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testing, setTesting] = useState(null);

  useEffect(() => { fetchTemplates(); }, []);

  async function fetchTemplates() {
    setLoading(true);
    try {
      const { data } = await adminApi.getEmailTemplates();
      setTemplates(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function startEdit(tpl) {
    setEditingId(tpl.id);
    setForm({ subject: tpl.subject, html_body: tpl.html_body, text_body: tpl.text_body || '' });
    setError('');
    setSuccess('');
  }

  function cancelEdit() {
    setEditingId(null);
    setForm({ subject: '', html_body: '', text_body: '' });
    setError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await adminApi.updateEmailTemplate(editingId, form);
      setEditingId(null);
      setForm({ subject: '', html_body: '', text_body: '' });
      setSuccess('Template updated');
      setTimeout(() => setSuccess(''), 3000);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template');
    }
  }

  async function handleTest(id) {
    setTesting(id);
    setError('');
    setSuccess('');
    try {
      const { data } = await adminApi.testEmailTemplate(id);
      setSuccess(data.message || 'Test email sent');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send test email');
    } finally {
      setTesting(null);
    }
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <h1>Email Templates</h1>
          <p>View and edit system email templates. Changes apply to all future emails.</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {editingId && (() => {
          const tpl = templates.find(t => t.id === editingId);
          return (
            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4 style={{ margin: 0 }}>Edit: {tpl?.template_name}</h4>
                <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
              </div>

              {tpl?.placeholders && tpl.placeholders.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginRight: 8 }}>Placeholders:</span>
                  {tpl.placeholders.map(p => (
                    <span key={p} style={{
                      display: 'inline-block', padding: '2px 8px', margin: '2px 4px',
                      background: 'var(--navy-light)', border: '1px solid var(--border)',
                      borderRadius: 4, fontSize: '0.75rem', color: 'var(--amber)', fontFamily: 'monospace'
                    }}>
                      {'{{' + p + '}}'}
                    </span>
                  ))}
                </div>
              )}

              <form onSubmit={handleSave}>
                <div className="form-group">
                  <label>Subject</label>
                  <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>HTML Body</label>
                  <textarea
                    value={form.html_body}
                    onChange={e => setForm({ ...form, html_body: e.target.value })}
                    rows={14}
                    required
                    style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                  />
                </div>
                <div className="form-group">
                  <label>Plain Text Body</label>
                  <textarea
                    value={form.text_body}
                    onChange={e => setForm({ ...form, text_body: e.target.value })}
                    rows={4}
                    style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn btn-primary btn-sm">Save</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleTest(editingId)} disabled={testing === editingId}>
                    {testing === editingId ? 'Sending...' : 'Send Test'}
                  </button>
                </div>
              </form>
            </div>
          );
        })()}

        {loading ? <div className="spinner" /> : (
          <div className="card">
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr><th>Name</th><th>Key</th><th>Subject</th><th>Last Updated</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {templates.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No email templates found</td></tr>
                  ) : templates.map(tpl => (
                    <tr key={tpl.id}>
                      <td style={{ fontWeight: 500 }}>{tpl.template_name}</td>
                      <td data-label="Key" style={{ color: 'var(--amber)', fontFamily: 'monospace', fontSize: '0.8rem' }}>{tpl.template_key}</td>
                      <td data-label="Subject" style={{ color: '#8a9bb5', fontSize: '0.82rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.subject}</td>
                      <td data-label="Updated" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{tpl.updated_at ? new Date(tpl.updated_at).toLocaleDateString() : '—'}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(tpl)}>Edit</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleTest(tpl.id)} disabled={testing === tpl.id}>
                            {testing === tpl.id ? 'Sending...' : 'Test'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="alert alert-info" style={{ marginTop: 24, fontSize: '0.8rem' }}>
          <strong>Note:</strong> Use {'{{placeholderName}}'} syntax in templates. "Send Test" sends the template to your own email with sample data.
        </div>
      </div>
    </div>
  );
}

// ─── AUDIT LOG PAGE ─────────────────────────────────────────
const ACTION_OPTIONS = [
  'user.create', 'user.update', 'user.activate', 'user.deactivate', 'user.delete', 'user.anonymize',
  'event.status_change', 'event.payment', 'admin.event.update_payment_status',
  'team.update', 'team.delete', 'team.remove',
  'tier.create', 'tier.update', 'tier.delete',
  'coupon.create', 'coupon.update', 'coupon.delete',
  'theme.update', 'theme.logo_upload', 'theme.logo_delete', 'theme.reset',
  'template.update', 'template.test'
];
const ENTITY_OPTIONS = ['event', 'organizer', 'participant', 'team', 'pricing_tier', 'coupon', 'theme', 'email_template'];

export function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ search: '', action: '', entity_type: '', actor_email: '', from: '', to: '' });
  const [expandedId, setExpandedId] = useState(null);
  const [actors, setActors] = useState([]);

  useEffect(() => {
    adminApi.getAuditLogActors().then(res => setActors(res.data.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchLogs(); }, [pagination.page, filters]);

  async function fetchLogs() {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: 30 };
      if (filters.search) params.search = filters.search;
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.actor_email) params.actor_email = filters.actor_email;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;

      const { data } = await adminApi.getAuditLog(params);
      setLogs(data.data);
      setPagination(prev => ({ ...prev, pages: data.pagination.pages, total: data.pagination.total }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function updateFilter(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }

  async function handleExport() {
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.actor_email) params.actor_email = filters.actor_email;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;

      const response = await adminApi.exportAuditLog(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit-log.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export audit log');
    }
  }

  const filterInputStyle = {
    padding: '8px 12px', background: '#1e2d3d', border: '1px solid #2a3f56',
    borderRadius: 8, color: '#f0f2f5', fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.85rem'
  };

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header page-header-row">
          <div>
            <h1>Audit Log</h1>
            <p>Track all admin actions. Entries older than 12 months are automatically purged.</p>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={handleExport}>Export CSV</button>
        </div>

        {/* Filters */}
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            <input
              type="text"
              placeholder="Search..."
              value={filters.search}
              onChange={e => updateFilter('search', e.target.value)}
              style={{ ...filterInputStyle, gridColumn: 'span 2' }}
            />
            <select value={filters.action} onChange={e => updateFilter('action', e.target.value)} style={filterInputStyle}>
              <option value="">All Actions</option>
              {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={filters.entity_type} onChange={e => updateFilter('entity_type', e.target.value)} style={filterInputStyle}>
              <option value="">All Entity Types</option>
              {ENTITY_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select value={filters.actor_email} onChange={e => updateFilter('actor_email', e.target.value)} style={filterInputStyle}>
              <option value="">All Users</option>
              {actors.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <input type="datetime-local" value={filters.from} onChange={e => updateFilter('from', e.target.value)} style={filterInputStyle} placeholder="From" />
            <input type="datetime-local" value={filters.to} onChange={e => updateFilter('to', e.target.value)} style={filterInputStyle} placeholder="To" />
          </div>
        </div>

        {loading ? <div className="spinner" /> : (
          <>
            <div className="card">
              <div className="table-wrap">
                <table className="responsive-table">
                  <thead>
                    <tr><th>Date/Time</th><th>Admin</th><th>Action</th><th>Entity Type</th><th>Entity ID</th><th>Details</th></tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No audit log entries found</td></tr>
                    ) : logs.map(log => (
                      <React.Fragment key={log.id}>
                        <tr onClick={() => setExpandedId(expandedId === log.id ? null : log.id)} style={{ cursor: 'pointer' }}>
                          <td style={{ fontSize: '0.82rem', color: '#8a9bb5', whiteSpace: 'nowrap' }}>
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td data-label="Admin" style={{ fontSize: '0.82rem', color: '#8a9bb5' }}>{log.actor_email || '—'}</td>
                          <td data-label="Action">
                            <span className={`badge ${log.action.includes('delete') || log.action.includes('anonymize') ? 'badge-draft' : log.action.includes('create') ? 'badge-active' : 'badge-pending'}`}>
                              {log.action}
                            </span>
                          </td>
                          <td data-label="Entity" style={{ fontSize: '0.82rem' }}>{log.entity_type || '—'}</td>
                          <td data-label="ID" style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#8a9bb5', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.entity_id ? log.entity_id.slice(0, 8) + '...' : '—'}
                          </td>
                          <td data-label="Details" style={{ fontSize: '0.78rem', color: '#8a9bb5', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {JSON.stringify(log.details || {}).slice(0, 60)}
                          </td>
                        </tr>
                        {expandedId === log.id && (
                          <tr>
                            <td colSpan={6} style={{ background: '#1a2a3a', padding: 16 }}>
                              <div style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: '#f0f2f5', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {JSON.stringify(log.details, null, 2)}
                              </div>
                              {log.ip_address && (
                                <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#8a9bb5' }}>IP: {log.ip_address}</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </button>
                <span style={{ padding: '8px 12px', fontSize: '0.85rem', color: '#8a9bb5' }}>
                  Page {pagination.page} of {pagination.pages} ({pagination.total} entries)
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const DISPLAY_FONTS = ['Oswald', 'Montserrat', 'Bebas Neue', 'Roboto Condensed'];
const BODY_FONTS = ['IBM Plex Sans', 'Inter', 'Open Sans', 'Lato'];

const COLOR_GROUPS = [
  {
    label: 'Primary',
    vars: [
      { key: '--navy-deep', label: 'Deep Background' },
      { key: '--navy-mid', label: 'Brand Navy' },
      { key: '--navy-light', label: 'Light Navy' },
      { key: '--navy-card', label: 'Card Background' },
    ]
  },
  {
    label: 'Accent',
    vars: [
      { key: '--amber', label: 'Amber' },
      { key: '--amber-light', label: 'Amber Light' },
      { key: '--amber-dim', label: 'Amber Dim' },
    ]
  },
  {
    label: 'Text',
    vars: [
      { key: '--text-primary', label: 'Primary Text' },
      { key: '--text-secondary', label: 'Secondary Text' },
      { key: '--text-muted', label: 'Muted Text' },
    ]
  },
  {
    label: 'Status',
    vars: [
      { key: '--green', label: 'Success' },
      { key: '--red', label: 'Danger' },
      { key: '--blue', label: 'Info' },
      { key: '--warning', label: 'Warning' },
    ]
  },
  {
    label: 'Surface',
    vars: [
      { key: '--border', label: 'Border' },
      { key: '--surface', label: 'Surface' },
      { key: '--surface-hover', label: 'Surface Hover' },
    ]
  }
];

export function ThemeSettingsPage() {
  const { settings: savedSettings, overrides: savedOverrides, siteName: savedSiteName, logoUrl: savedLogoUrl, siteUrl: savedSiteUrl, loadTheme, previewSettings, revertPreview, DEFAULT_SETTINGS } = useTheme();

  const [siteName, setSiteName] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [localSettings, setLocalSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);
  const hasUnsavedRef = useRef(false);

  // Sync from saved theme on mount
  useEffect(() => {
    setSiteName(savedSiteName);
    setLogoUrl(savedLogoUrl);
    setSiteUrl(savedSiteUrl);
    setLocalSettings({ ...DEFAULT_SETTINGS, ...savedOverrides });
  }, [savedSiteName, savedLogoUrl, savedSiteUrl, savedOverrides, DEFAULT_SETTINGS]);

  // Revert preview on unmount if unsaved
  useEffect(() => {
    return () => {
      if (hasUnsavedRef.current) {
        revertPreview();
      }
    };
  }, [revertPreview]);

  function updateSetting(key, value) {
    const next = { ...localSettings, [key]: value };
    setLocalSettings(next);
    hasUnsavedRef.current = true;
    previewSettings({ ...DEFAULT_SETTINGS, ...next });
  }

  function getFontValue(cssVar) {
    const val = localSettings[cssVar] || DEFAULT_SETTINGS[cssVar] || '';
    return val.replace(/'/g, '').split(',')[0].trim();
  }

  function setFontValue(cssVar, fontName) {
    updateSetting(cssVar, `'${fontName}', sans-serif`);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Build overrides: only keys that differ from defaults
      const overrides = {};
      Object.entries(localSettings).forEach(([k, v]) => {
        if (v !== DEFAULT_SETTINGS[k]) overrides[k] = v;
      });

      await adminApi.updateTheme({ site_name: siteName, site_url: siteUrl || null, settings: overrides });
      hasUnsavedRef.current = false;
      await loadTheme();
      setSuccess('Theme saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!window.confirm('Reset all theme settings to defaults? This cannot be undone.')) return;
    setSaving(true);
    setError('');
    try {
      await adminApi.resetTheme();
      hasUnsavedRef.current = false;
      await loadTheme();
      setSuccess('Theme reset to defaults');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset theme');
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('logo', file);
      await adminApi.uploadLogo(formData);
      await loadTheme();
      setSuccess('Logo uploaded');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleLogoDelete() {
    if (!window.confirm('Remove the logo and revert to the default emoji?')) return;
    try {
      await adminApi.deleteLogo();
      await loadTheme();
      setSuccess('Logo removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove logo');
    }
  }

  const colorInputStyle = {
    width: 40, height: 40, padding: 2, border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer'
  };

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <h1>Theme Settings</h1>
          <p>Customize the site's visual identity. Changes preview live as you edit.</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* BRANDING */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Branding</h3>
          <div className="grid-2col" style={{ alignItems: 'start' }}>
            <div>
              <div className="form-group">
                <label>Site Logo</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 'var(--radius-md)',
                    background: 'var(--navy-light)', border: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0
                  }}>
                    {savedLogoUrl ? (
                      <img src={assetUrl(savedLogoUrl)} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <IconFish size="32px" />
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                    <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      {uploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    {savedLogoUrl && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={handleLogoDelete}>
                        Remove Logo
                      </button>
                    )}
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>PNG, JPG, SVG, WebP. Max 2MB.</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="form-group">
                <label>Site Name</label>
                <input
                  value={siteName}
                  onChange={e => { setSiteName(e.target.value); hasUnsavedRef.current = true; }}
                  maxLength={100}
                  placeholder="DerbyScores"
                />
              </div>
              <div className="form-group">
                <label>Site URL</label>
                <input
                  value={siteUrl}
                  onChange={e => { setSiteUrl(e.target.value); hasUnsavedRef.current = true; }}
                  maxLength={500}
                  placeholder="https://derbyscores.com"
                />
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Used for email verification links. Leave blank for localhost.</span>
              </div>
            </div>
          </div>
        </div>

        {/* COLORS */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Colors</h3>
          {COLOR_GROUPS.map(group => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 10, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{group.label}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {group.vars.map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="color"
                      value={localSettings[key] || DEFAULT_SETTINGS[key]}
                      onChange={e => updateSetting(key, e.target.value)}
                      style={colorInputStyle}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 2 }}>{label}</div>
                      <input
                        value={localSettings[key] || DEFAULT_SETTINGS[key]}
                        onChange={e => updateSetting(key, e.target.value)}
                        style={{
                          width: '100%', padding: '4px 8px', fontSize: '0.75rem',
                          background: 'var(--navy-light)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* TYPOGRAPHY */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Typography</h3>
          <div className="grid-2col">
            <div className="form-group">
              <label>Display Font (Headings)</label>
              <select value={getFontValue('--font-display')} onChange={e => setFontValue('--font-display', e.target.value)}>
                {DISPLAY_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Body Font</label>
              <select value={getFontValue('--font-body')} onChange={e => setFontValue('--font-body', e.target.value)}>
                {BODY_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div style={{ padding: 16, background: 'var(--navy-light)', borderRadius: 'var(--radius-md)', marginTop: 8 }}>
            <h4 style={{ marginBottom: 4 }}>Preview Heading Text</h4>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
              This is body text using the selected body font. The quick brown fox jumps over the lazy dog.
            </p>
          </div>
        </div>

        {/* BORDER RADIUS */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Border Radius</h3>
          <div className="grid-4col">
            {[
              { key: '--radius-sm', label: 'Small' },
              { key: '--radius-md', label: 'Medium' },
              { key: '--radius-lg', label: 'Large' },
              { key: '--radius-xl', label: 'Extra Large' },
            ].map(({ key, label }) => (
              <div className="form-group" key={key}>
                <label>{label}</label>
                <input
                  value={localSettings[key] || DEFAULT_SETTINGS[key]}
                  onChange={e => updateSetting(key, e.target.value)}
                  placeholder="e.g. 10px"
                />
              </div>
            ))}
          </div>
        </div>

        {/* ACTIONS */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleReset} disabled={saving}>
            Reset to Defaults
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ALL EVENTS PAGE (admin) ────────────────────────────────
export function AllEventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editTournaments, setEditTournaments] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const { page, setPage, limit, total, setTotal, sortBy, sortOrder, handleSort, handleLimitChange, totalPages } = useAdminTable(null);

  useEffect(() => { setPage(1); }, [search, statusFilter]);
  useEffect(() => { fetchEvents(); }, [search, statusFilter, page, limit, sortBy, sortOrder]);

  async function fetchEvents() {
    setLoading(true);
    try {
      const params = { search, page, limit };
      if (statusFilter) params.status = statusFilter;
      if (sortBy) { params.sortBy = sortBy; params.sortOrder = sortOrder; }
      const { data } = await adminApi.getEvents(params);
      setEvents(data.data);
      setTotal(data.total || data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkPaid(eventId, eventName, e) {
    e.stopPropagation();
    if (!window.confirm(`Mark "${eventName}" as paid? This will also activate the event if it's pending payment.`)) return;
    try {
      await adminApi.updateEventPaymentStatus(eventId, { payment_status: 'paid' });
      fetchEvents();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update payment status');
    }
  }

  async function handleMarkUnpaid(eventId, eventName, e) {
    e.stopPropagation();
    if (!window.confirm(`Mark "${eventName}" as unpaid?`)) return;
    try {
      await adminApi.updateEventPaymentStatus(eventId, { payment_status: 'unpaid' });
      fetchEvents();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update payment status');
    }
  }

  async function handleDelete(eventId, eventName, e) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${eventName}"? This will permanently delete all tournaments, catches, registrations, teams, and sponsors. This cannot be undone.`)) return;
    setDeleting(eventId);
    try {
      await eventsApi.deleteEvent(eventId);
      fetchEvents();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete event');
    } finally {
      setDeleting(null);
    }
  }

  function toLocalDatetime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  }

  // Convert datetime-local value back to ISO with timezone for saving
  function fromLocalDatetime(localStr) {
    if (!localStr) return localStr;
    // datetime-local gives "YYYY-MM-DDTHH:mm" in browser local time
    // new Date() interprets it as local time, then toISOString() gives UTC
    const d = new Date(localStr);
    if (isNaN(d.getTime())) return localStr;
    return d.toISOString();
  }

  async function handleEdit(ev, e) {
    e.stopPropagation();
    setEditError('');
    setEditForm({
      name: ev.name || '',
      description: ev.description || '',
      location: ev.location || '',
      start_date: toLocalDatetime(ev.start_date),
      end_date: toLocalDatetime(ev.end_date),
      waiver_text: ev.waiver_text || '',
      terms_and_conditions: ev.terms_and_conditions || '',
      event_type: ev.event_type || 'team',
    });
    setEditImageUrl(ev.featured_image_url || null);
    setEditingId(ev.id);
    try {
      const res = await tournamentApi.getTournaments(ev.id);
      setEditTournaments(res.data.data || []);
    } catch {
      setEditTournaments([]);
    }
  }

  async function handleImageUpload(file) {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!allowed.includes(file.type)) { setEditError('Image must be JPG, PNG, WebP, or AVIF'); return; }
    if (file.size > 4 * 1024 * 1024) { setEditError('Image must be under 4MB'); return; }
    setImageUploading(true);
    setEditError('');
    try {
      const fd = new FormData();
      fd.append('featured_image', file);
      const res = await eventsApi.uploadFeaturedImage(editingId, fd);
      setEditImageUrl(res.data.data.featured_image_url);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleImageRemove() {
    if (!window.confirm('Remove featured image?')) return;
    setImageUploading(true);
    try {
      await eventsApi.deleteFeaturedImage(editingId);
      setEditImageUrl(null);
    } catch {
      setEditError('Failed to remove image');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleEditSave(e) {
    e.preventDefault();
    if (!editForm.name?.trim()) {
      setEditError('Event name is required');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const payload = {
        ...editForm,
        start_date: fromLocalDatetime(editForm.start_date),
        end_date: fromLocalDatetime(editForm.end_date),
      };
      await eventsApi.updateEvent(editingId, payload);
      setEditingId(null);
      fetchEvents();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update event');
    } finally {
      setEditSaving(false);
    }
  }

  function handleEditCancel(e) {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(null);
    setEditError('');
  }

  const formatBlocked = editTournaments.length > 0 && editForm.event_type === 'team';
  const inputStyle = { width: '100%', padding: '8px 12px', background: '#152232', border: '1px solid #2a3f56', borderRadius: 6, color: '#f0f2f5', fontSize: '0.85rem' };
  const labelStyle = { display: 'block', color: '#8a9bb5', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };

  const statusTabs = [
    { value: '', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_payment', label: 'Pending Payment' },
    { value: 'active', label: 'Active' },
    { value: 'live', label: 'Live' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <h1>All Events</h1>
          <p>View and manage all events across all organizers</p>
        </div>

        <div className="card">
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a3f56' }}>
            <input
              type="text"
              placeholder="Search events or organizers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', maxWidth: 400, marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {statusTabs.map(tab => (
                <button
                  key={tab.value}
                  className={`btn btn-sm ${statusFilter === tab.value ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setStatusFilter(tab.value)}
                  style={{ padding: '4px 12px', fontSize: '0.75rem' }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? <div className="spinner" style={{ margin: '40px auto' }} /> : (
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <SortTh label="Event" sortKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortTh label="Organizer" sortKey="organizer" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortTh label="Date" sortKey="start_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortTh label="Payment" sortKey="payment_status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Revenue</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No events found</td></tr>
                  ) : events.map(ev => (
                    <React.Fragment key={ev.id}>
                      <tr onClick={() => navigate(`/events/${ev.id}`)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 500 }}>{ev.name}</td>
                        <td data-label="Organizer" style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>{ev.organizer_name}</td>
                        <td data-label="Date" style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>
                          {new Date(ev.start_date).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td data-label="Status">
                          <span className={`badge badge-${ev.status}`}>{eventStatusLabel(ev.status)}</span>
                        </td>
                        <td data-label="Payment">
                          {ev.payment_status === 'paid' ? (
                            <span
                              className="badge badge-paid"
                              style={{ cursor: 'pointer' }}
                              title="Click to mark as unpaid"
                              onClick={(e) => handleMarkUnpaid(ev.id, ev.name, e)}
                            >paid</span>
                          ) : (
                            <span
                              className="badge badge-unpaid"
                              style={{ cursor: 'pointer', borderBottom: '1px dashed' }}
                              title="Click to mark as paid"
                              onClick={(e) => handleMarkPaid(ev.id, ev.name, e)}
                            >{ev.payment_status || 'unpaid'}</span>
                          )}
                        </td>
                        <td data-label="Revenue" style={{ color: '#e8a838' }}>${parseFloat(ev.amount_paid || 0).toFixed(2)}</td>
                        <td data-label="Actions">
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#e8a838' }}
                              onClick={(e) => handleEdit(ev, e)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ padding: '4px 12px', fontSize: '0.75rem', color: '#e85d5d' }}
                              onClick={(e) => handleDelete(ev.id, ev.name, e)}
                              disabled={deleting === ev.id}
                            >
                              {deleting === ev.id ? '...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editingId === ev.id && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <form onSubmit={handleEditSave} onClick={e => e.stopPropagation()} style={{
                              background: '#1a2a3a', padding: 20,
                              display: 'flex', flexDirection: 'column', gap: 14
                            }}>
                              {editError && (
                                <div className="alert alert-danger" style={{ margin: 0, padding: '8px 14px', fontSize: '0.85rem' }}>{editError}</div>
                              )}

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div>
                                  <label style={labelStyle}>Event Name *</label>
                                  <input style={inputStyle} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                                </div>
                                <div>
                                  <label style={labelStyle}>Location</label>
                                  <input style={inputStyle} value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
                                </div>
                              </div>

                              <div>
                                <label style={labelStyle}>Description</label>
                                <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div>
                                  <label style={labelStyle}>Start Date</label>
                                  <input type="datetime-local" style={inputStyle} value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} />
                                </div>
                                <div>
                                  <label style={labelStyle}>End Date</label>
                                  <input type="datetime-local" style={inputStyle} value={editForm.end_date} onChange={e => setEditForm(f => ({ ...f, end_date: e.target.value }))} />
                                </div>
                              </div>

                              <div>
                                <label style={labelStyle}>Event Waiver</label>
                                <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.waiver_text} onChange={e => setEditForm(f => ({ ...f, waiver_text: e.target.value }))} />
                              </div>

                              <div>
                                <label style={labelStyle}>Terms & Conditions</label>
                                <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={editForm.terms_and_conditions} onChange={e => setEditForm(f => ({ ...f, terms_and_conditions: e.target.value }))} />
                              </div>

                              <div>
                                <label style={labelStyle}>Event Format</label>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                  <button type="button" style={{
                                    padding: '6px 16px', borderRadius: 6, fontSize: '0.85rem', cursor: formatBlocked ? 'not-allowed' : 'pointer',
                                    background: editForm.event_type === 'team' ? '#e8a838' : '#152232',
                                    color: editForm.event_type === 'team' ? '#0a1628' : '#8a9bb5',
                                    border: editForm.event_type === 'team' ? 'none' : '1px solid #2a3f56',
                                    opacity: formatBlocked && editForm.event_type !== 'team' ? 0.5 : 1
                                  }} disabled={formatBlocked} onClick={() => setEditForm(f => ({ ...f, event_type: 'team' }))}>Team</button>
                                  <button type="button" style={{
                                    padding: '6px 16px', borderRadius: 6, fontSize: '0.85rem', cursor: formatBlocked ? 'not-allowed' : 'pointer',
                                    background: editForm.event_type === 'individual' ? '#e8a838' : '#152232',
                                    color: editForm.event_type === 'individual' ? '#0a1628' : '#8a9bb5',
                                    border: editForm.event_type === 'individual' ? 'none' : '1px solid #2a3f56',
                                    opacity: formatBlocked && editForm.event_type !== 'individual' ? 0.5 : 1
                                  }} disabled={formatBlocked} onClick={() => setEditForm(f => ({ ...f, event_type: 'individual' }))}>Individual</button>
                                </div>
                                {formatBlocked && (
                                  <div style={{ color: '#e85d5d', fontSize: '0.75rem', marginTop: 6 }}>
                                    Delete existing tournaments before changing event format.
                                  </div>
                                )}
                              </div>

                              <div>
                                <label style={labelStyle}>Featured Image</label>
                                {editImageUrl ? (
                                  <div style={{ position: 'relative' }}>
                                    <img src={assetUrl(editImageUrl)} alt="Featured" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid #2a3f56' }} />
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                      <label style={{ color: '#4a9eff', fontSize: '0.8rem', cursor: imageUploading ? 'default' : 'pointer', opacity: imageUploading ? 0.5 : 1 }}>
                                        {imageUploading ? 'Uploading...' : 'Replace'}
                                        <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={e => handleImageUpload(e.target.files[0])} disabled={imageUploading} style={{ display: 'none' }} />
                                      </label>
                                      <button type="button" onClick={handleImageRemove} disabled={imageUploading} style={{ background: 'none', border: 'none', color: '#e85d5d', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>Remove</button>
                                    </div>
                                  </div>
                                ) : (
                                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '16px 12px', border: '2px dashed #2a3f56', borderRadius: 8, cursor: imageUploading ? 'default' : 'pointer', color: '#8a9bb5', fontSize: '0.82rem', opacity: imageUploading ? 0.5 : 1 }}>
                                    {imageUploading ? 'Uploading...' : 'Click to upload image (JPG, PNG, WebP, AVIF — max 4MB)'}
                                    <input type="file" accept=".jpg,.jpeg,.png,.webp,.avif" onChange={e => handleImageUpload(e.target.files[0])} disabled={imageUploading} style={{ display: 'none' }} />
                                  </label>
                                )}
                              </div>

                              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={handleEditCancel} style={{ padding: '6px 16px' }}>Cancel</button>
                                <button type="submit" className="btn btn-primary btn-sm" disabled={editSaving} style={{ padding: '6px 16px' }}>
                                  {editSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <PaginationBar page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={handleLimitChange} />
        </div>
      </div>
    </div>
  );
}

// ─── SPONSORS PAGE (global sponsor library) ─────────────────
export function SponsorsPage() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ sponsor_name: '', logo_url: '', website_url: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { page, setPage, limit, total, setTotal, sortBy, sortOrder, handleSort, handleLimitChange, totalPages } = useAdminTable(null);

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { fetchSponsors(); }, [search, page, limit, sortBy, sortOrder]);

  async function fetchSponsors() {
    setLoading(true);
    try {
      const params = { search, page, limit };
      if (sortBy) { params.sortBy = sortBy; params.sortOrder = sortOrder; }
      const { data } = await adminApi.getSponsors(params);
      setSponsors(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingId(null);
    setForm({ sponsor_name: '', logo_url: '', website_url: '' });
    setError('');
    setShowForm(true);
  }

  function openEdit(sponsor) {
    setEditingId(sponsor.id);
    setForm({ sponsor_name: sponsor.sponsor_name, logo_url: sponsor.logo_url, website_url: sponsor.website_url || '' });
    setError('');
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({ sponsor_name: '', logo_url: '', website_url: '' });
    setError('');
  }

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const { data } = await adminApi.uploadSponsorLogo(fd);
      setForm(f => ({ ...f, logo_url: data.logo_url }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.sponsor_name.trim() || !form.logo_url) {
      setError('Sponsor name and logo are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await adminApi.updateSponsor(editingId, form);
      } else {
        await adminApi.createSponsor(form);
      }
      cancelForm();
      fetchSponsors();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save sponsor');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete sponsor "${name}" from the global library? This will not remove it from events already using it.`)) return;
    try {
      await adminApi.deleteSponsor(id);
      fetchSponsors();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete sponsor');
    }
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1>Sponsors</h1>
            <p>Manage the global sponsor library</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => showForm ? cancelForm() : openCreate()}>
            {showForm ? 'Cancel' : '+ Add Sponsor'}
          </button>
        </div>

        {/* Create/Edit form */}
        {showForm && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 16 }}>{editingId ? 'Edit Sponsor' : 'Add Sponsor'}</h4>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Sponsor Name *</label>
                <input value={form.sponsor_name} onChange={e => setForm(f => ({ ...f, sponsor_name: e.target.value }))} placeholder="e.g. Bass Pro Shops" required />
              </div>
              <div className="form-group">
                <label>Logo *</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    value={form.logo_url}
                    onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
                    placeholder="https://... or upload a file"
                    required
                    style={{ flex: 1 }}
                  />
                  <input type="file" id="admin-sponsor-logo" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleLogoUpload} disabled={uploading} style={{ display: 'none' }} />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => document.getElementById('admin-sponsor-logo').click()} disabled={uploading} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
                {form.logo_url && (
                  <div style={{ marginTop: 8 }}>
                    <img src={assetUrl(form.logo_url)} alt="Preview" style={{ height: 48, maxWidth: 140, objectFit: 'contain', borderRadius: 6, background: '#1e2d3d', padding: 4 }} onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input value={form.website_url} onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))} placeholder="https://example.com" />
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving} style={{ marginTop: 8 }}>
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Sponsor'}
              </button>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <input
            type="text"
            placeholder="Search sponsors..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, color: '#f0f2f5', fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.88rem' }}
          />
        </div>

        {loading ? <div className="spinner" /> : (
          <div className="card">
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Logo</th>
                    <SortTh label="Sponsor Name" sortKey="sponsor_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Website</th>
                    <SortTh label="Created" sortKey="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sponsors.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No sponsors found</td></tr>
                  ) : sponsors.map(s => (
                    <tr key={s.id}>
                      <td>
                        <img src={assetUrl(s.logo_url)} alt={s.sponsor_name} style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 6, background: '#1e2d3d', padding: 2 }} />
                      </td>
                      <td style={{ fontWeight: 500 }}>{s.sponsor_name}</td>
                      <td data-label="Website" style={{ fontSize: '0.82rem' }}>
                        {s.website_url ? (
                          <a href={s.website_url} target="_blank" rel="noopener noreferrer" style={{ color: '#4a9eff', textDecoration: 'none' }}>{s.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>
                        ) : (
                          <span style={{ color: '#5c6d83' }}>—</span>
                        )}
                      </td>
                      <td data-label="Created" style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>
                        {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleDelete(s.id, s.sponsor_name)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={handleLimitChange} />
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// INVOICES PAGE
// ═══════════════════════════════════════════════════════════════
export function InvoicesPage() {
  const AdminInvoices = require('../components/AdminInvoices').default;
  return (
    <div className="page">
      <AdminInvoices />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADVERT BOARDS PAGE
// ═══════════════════════════════════════════════════════════════
export function AdvertBoardsPage() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', content_html: '', display_duration_seconds: 30, display_order: 0, is_active: true });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedBoard, setExpandedBoard] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [events, setEvents] = useState([]);
  const [assignEventId, setAssignEventId] = useState('__all__');

  useEffect(() => { fetchBoards(); fetchEvents(); }, []);

  async function fetchBoards() {
    setLoading(true);
    try {
      const { data } = await adminApi.getAdvertBoards();
      setBoards(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchEvents() {
    try {
      const { data } = await adminApi.getEvents({ limit: 200 });
      setEvents(data.data || []);
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchAssignments(boardId) {
    try {
      const { data } = await adminApi.getAdvertBoardAssignments(boardId);
      setAssignments(data.data || []);
    } catch (err) {
      console.error(err);
    }
  }

  function openCreate() {
    setEditingId(null);
    setForm({ title: '', content_html: '', display_duration_seconds: 30, display_order: 0, is_active: true });
    setError('');
    setShowForm(true);
  }

  function openEdit(board) {
    setEditingId(board.id);
    setForm({
      title: board.title,
      content_html: board.content_html,
      display_duration_seconds: board.display_duration_seconds,
      display_order: board.display_order,
      is_active: board.is_active
    });
    setError('');
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({ title: '', content_html: '', display_duration_seconds: 30, display_order: 0, is_active: true });
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await adminApi.updateAdvertBoard(editingId, form);
      } else {
        await adminApi.createAdvertBoard(form);
      }
      cancelForm();
      fetchBoards();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save advert board');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, title) {
    if (!window.confirm(`Delete advert board "${title}"? This will also remove all event assignments.`)) return;
    try {
      await adminApi.deleteAdvertBoard(id);
      if (expandedBoard === id) setExpandedBoard(null);
      fetchBoards();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete advert board');
    }
  }

  async function toggleActive(board) {
    try {
      await adminApi.updateAdvertBoard(board.id, { is_active: !board.is_active });
      fetchBoards();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to toggle status');
    }
  }

  async function toggleAssignments(boardId) {
    if (expandedBoard === boardId) {
      setExpandedBoard(null);
      setAssignments([]);
    } else {
      setExpandedBoard(boardId);
      fetchAssignments(boardId);
    }
  }

  async function handleAssign(boardId) {
    try {
      await adminApi.createAdvertBoardAssignment(boardId, {
        event_id: assignEventId === '__all__' ? null : assignEventId
      });
      setAssignEventId('__all__');
      fetchAssignments(boardId);
      fetchBoards();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign');
    }
  }

  async function handleUnassign(assignmentId, boardId) {
    try {
      await adminApi.deleteAdvertBoardAssignment(assignmentId);
      fetchAssignments(boardId);
      fetchBoards();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove assignment');
    }
  }

  const durationMarks = [10, 15, 20, 30, 45, 60, 90, 120];

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1>Advert Boards</h1>
            <p>Manage advertising boards displayed in scoreboard rotation</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => showForm ? cancelForm() : openCreate()}>
            {showForm ? 'Cancel' : '+ New Advert Board'}
          </button>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 16 }}>{editingId ? 'Edit Advert Board' : 'Create Advert Board'}</h4>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. TrollOG Promo" required />
              </div>

              {/* HTML editor with live preview */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0, flex: '1 1 0', minWidth: 0 }}>
                  <label>HTML Content</label>
                  <textarea
                    value={form.content_html}
                    onChange={e => setForm(f => ({ ...f, content_html: e.target.value }))}
                    placeholder="<div>Your ad content here...</div>"
                    rows={12}
                    style={{ fontFamily: 'monospace', fontSize: '0.82rem', resize: 'vertical' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0, width: '50vw', minWidth: '50vw', maxWidth: '50vw', flexShrink: 0 }}>
                  <label>Preview</label>
                  <div
                    style={{
                      background: '#000', border: '1px solid #2a3f56', borderRadius: 8, padding: 16,
                      height: 280, overflow: 'auto', color: '#fff',
                      boxSizing: 'border-box'
                    }}
                    dangerouslySetInnerHTML={{ __html: form.content_html }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Display Duration: {form.display_duration_seconds}s</label>
                  <input
                    type="range" min={10} max={120} step={5}
                    value={form.display_duration_seconds}
                    onChange={e => setForm(f => ({ ...f, display_duration_seconds: parseInt(e.target.value) }))}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#8a9bb5' }}>
                    <span>10s</span><span>60s</span><span>120s</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input type="number" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))} min={0} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                    Active
                  </label>
                </div>
              </div>

              <button type="submit" className="btn btn-primary btn-sm" disabled={saving} style={{ marginTop: 8 }}>
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Board'}
              </button>
            </form>
          </div>
        )}

        {/* Board List */}
        {loading ? <div className="spinner" /> : (
          <div className="card">
            <div className="table-wrap">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th style={{ width: 80 }}>Order</th>
                    <th style={{ width: 90 }}>Duration</th>
                    <th style={{ width: 90 }}>Status</th>
                    <th style={{ width: 100 }}>Assigned</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {boards.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#8a9bb5', padding: 40 }}>No advert boards yet</td></tr>
                  ) : boards.map(b => (
                    <React.Fragment key={b.id}>
                      <tr>
                        <td style={{ fontWeight: 500 }}>{b.title}</td>
                        <td data-label="Order">{b.display_order}</td>
                        <td data-label="Duration">{b.display_duration_seconds}s</td>
                        <td data-label="Status">
                          <span
                            onClick={() => toggleActive(b)}
                            style={{
                              cursor: 'pointer', padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600,
                              background: b.is_active ? 'rgba(52,199,89,0.15)' : 'rgba(255,69,58,0.15)',
                              color: b.is_active ? '#34c759' : '#ff453a'
                            }}
                          >
                            {b.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td data-label="Assigned">
                          <button
                            className="btn btn-sm"
                            onClick={() => toggleAssignments(b.id)}
                            style={{
                              background: 'transparent',
                              border: '1px solid #e8a838',
                              color: '#e8a838',
                              fontWeight: 600,
                              borderRadius: 8
                            }}
                          >
                            {(b.assignment_count || 0) === 0
                              ? 'Assign Event(s)'
                              : `${b.assignment_count} event${b.assignment_count !== 1 ? 's' : ''}`}
                          </button>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(b)}>Edit</button>
                            <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d' }} onClick={() => handleDelete(b.id, b.title)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Assignments Panel */}
                      {expandedBoard === b.id && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0 }}>
                            <div style={{ background: '#1a2738', padding: 16, borderTop: '1px solid #2a3f56' }}>
                              <h5 style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#8a9bb5', fontWeight: 500 }}>Assign to event</h5>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <select
                                  value={assignEventId}
                                  onChange={e => setAssignEventId(e.target.value)}
                                  style={{ flex: 1, padding: '8px 12px', background: '#1e2d3d', border: '1px solid #2a3f56', borderRadius: 8, color: '#f0f2f5', fontSize: '0.85rem' }}
                                >
                                  <option value="__all__">All Events (global)</option>
                                  {events.map(ev => (
                                    <option key={ev.id} value={ev.id}>{ev.name} — {ev.status}</option>
                                  ))}
                                </select>
                                <button className="btn btn-primary btn-sm" onClick={() => handleAssign(b.id)}>Assign</button>
                              </div>
                              {assignments.length === 0 ? (
                                <p style={{ color: '#8a9bb5', fontSize: '0.85rem', margin: 0 }}>No assignments yet</p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {assignments.map(a => (
                                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#0f1923', borderRadius: 6, fontSize: '0.85rem' }}>
                                      <span>{a.event_id ? (a.event_name || 'Unknown event') : 'All Events (global)'}</span>
                                      <button className="btn btn-ghost btn-sm" style={{ color: '#e85d5d', fontSize: '0.78rem' }} onClick={() => handleUnassign(a.id, b.id)}>Remove</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BODIES OF WATER PAGE (circuit region vocabulary)
// ═══════════════════════════════════════════════════════════════
export function BodiesOfWaterPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', display_order: 0 });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    setLoading(true);
    try {
      const { data } = await bodiesOfWaterApi.list();
      setItems(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingId(null);
    setForm({ name: '', display_order: 0 });
    setError('');
    setShowForm(true);
  }

  function openEdit(item) {
    setEditingId(item.id);
    setForm({ name: item.name, display_order: item.display_order });
    setError('');
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await bodiesOfWaterApi.update(editingId, form);
      } else {
        await bodiesOfWaterApi.create(form);
      }
      setShowForm(false);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(item) {
    try {
      await bodiesOfWaterApi.update(item.id, { is_active: !item.is_active });
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update');
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await bodiesOfWaterApi.delete(item.id);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  }

  const cardStyle = {
    background: '#0d1b2a', borderRadius: 14, border: '1px solid #1e2d3d',
    overflow: 'hidden', maxWidth: 900, margin: '0 auto'
  };
  const headerStyle = {
    padding: '20px 24px', borderBottom: '1px solid #1e2d3d',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '1px solid #2a3f56', background: '#0a1628', color: '#f0f2f5',
    fontFamily: "'Oswald', sans-serif", fontSize: '0.95rem'
  };

  return (
    <div style={{ padding: '24px 20px', maxWidth: 960, margin: '0 auto' }}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontFamily: "'Oswald', sans-serif", color: '#f0f2f5', fontSize: '1.1rem' }}>
            Bodies of Water
          </h2>
          <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Add Body of Water</button>
        </div>

        {showForm && (
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e2d3d', background: '#0a1628' }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#8a9bb5', fontSize: '0.82rem' }}>Name *</label>
                <input
                  style={inputStyle}
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Lake Ontario"
                />
              </div>
              <div style={{ width: 100 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#8a9bb5', fontSize: '0.82rem' }}>Order</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={form.display_order}
                  onChange={e => setForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </div>
            {error && <div style={{ color: '#ef4444', marginTop: 8, fontSize: '0.85rem' }}>{error}</div>}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5c6d83' }}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5c6d83' }}>
            No bodies of water configured yet. Click "Add Body of Water" to get started.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e2d3d' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: '#8a9bb5', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1 }}>Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: '#8a9bb5', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1 }}>Order</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: '#8a9bb5', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1 }}>Events</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: '#8a9bb5', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1 }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', color: '#8a9bb5', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #1a2a3a' }}>
                    <td style={{ padding: '12px 16px', color: '#f0f2f5', fontFamily: "'Oswald', sans-serif" }}>{item.name}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#8a9bb5' }}>{item.display_order}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#8a9bb5' }}>{item.event_count}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                        background: item.is_active ? '#065f46' : '#7f1d1d',
                        color: item.is_active ? '#6ee7b7' : '#fca5a5'
                      }}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(item)} style={{ fontSize: '0.78rem' }}>Edit</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => handleToggleActive(item)} style={{ fontSize: '0.78rem' }}>
                          {item.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        {item.event_count === 0 && (
                          <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(item)} style={{ fontSize: '0.78rem', color: '#ef4444' }}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
